import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { karachiBusinessDate } from '@/lib/dates'
import { formatPKR } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildTransactions } from '@/features/transactions/transactions'
import { downloadBlob, shareFile } from '@/features/share/share'
import { computeReport, transactionsToCsv, type ReportFilters } from './reports'

const selCls =
  'rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 focus:border-sky-500 focus:outline-none'

type RangePreset = 'today' | '7d' | '30d' | 'all'

function rangeKeys(preset: RangePreset): { from?: string; to?: string } {
  const to = karachiBusinessDate()
  if (preset === 'today') return { from: to, to }
  if (preset === '7d') return { from: karachiBusinessDate(Date.now() - 6 * 86400000), to }
  if (preset === '30d') return { from: karachiBusinessDate(Date.now() - 29 * 86400000), to }
  return {}
}

/** Reports: filtered P&L summary + per-category breakdown + CSV/PDF export. */
export function ReportsPage() {
  const ws = getActiveWorkspaceId()

  const purchases = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).purchases.where('workspace_id').equals(ws).toArray() : []), [ws])
  const sales = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).sales.where('workspace_id').equals(ws).toArray() : []), [ws])
  const payments = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []), [ws])
  const expenses = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).expenses.where('workspace_id').equals(ws).toArray() : []), [ws])
  const devices = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).devices.where('workspace_id').equals(ws).toArray() : []), [ws])
  const categories = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).categories.where('workspace_id').equals(ws).toArray() : []), [ws])
  const contacts = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).contacts.where('workspace_id').equals(ws).toArray() : []), [ws])

  const [range, setRange] = useState<RangePreset>('30d')
  const [categoryId, setCategoryId] = useState('')
  const [brand, setBrand] = useState('')

  const inputs = {
    purchases: purchases ?? [],
    sales: sales ?? [],
    payments: payments ?? [],
    expenses: expenses ?? [],
    devices: devices ?? [],
    categories: categories ?? [],
    contacts: contacts ?? [],
  }
  const filters: ReportFilters = {
    ...rangeKeys(range),
    categoryId: categoryId || null,
    brandCode: brand || null,
  }
  const r = computeReport(inputs, filters)

  async function exportCsv() {
    const { from, to } = rangeKeys(range)
    const rows = buildTransactions(inputs).filter(
      (row) => (!from || row.dateKey >= from) && (!to || row.dateKey <= to),
    )
    const csv = transactionsToCsv(rows)
    const blob = new Blob([csv], { type: 'text/csv' })
    const name = `transactions-${from ?? 'all'}-${to ?? 'today'}.csv`
    const ok = await shareFile(blob, name, 'text/csv')
    if (!ok) downloadBlob(blob, name)
  }

  async function exportPdf() {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    let y = 48
    doc.setFontSize(16)
    doc.text('Mobile Trading Pro - Report', 40, y)
    y += 20
    doc.setFontSize(9)
    const { from, to } = rangeKeys(range)
    doc.text(`Period: ${from ?? 'start'} to ${to ?? 'today'} (Asia/Karachi)  |  Generated ${karachiBusinessDate()}`, 40, y)
    y += 24
    doc.setFontSize(11)
    const line = (label: string, value: string) => {
      doc.text(`${label}:`, 40, y)
      doc.text(value, 300, y)
      y += 18
    }
    line('Sales value', formatPKR(r.salesValue))
    line('Purchases', formatPKR(r.purchaseValue))
    line('Gross profit', formatPKR(r.grossProfit))
    line('Expenses', formatPKR(r.expensesTotal))
    line('Net profit', formatPKR(r.netProfit))
    line('Receivables (current)', formatPKR(r.receivables))
    line('Stock value (current)', formatPKR(r.stockValue))
    y += 12
    doc.setFontSize(12)
    doc.text('By category', 40, y)
    y += 18
    doc.setFontSize(10)
    for (const c of r.perCategory) {
      doc.text(`${c.name}: ${c.inStockCount} in stock | stock ${formatPKR(c.stockCost)} | profit ${formatPKR(c.realizedProfit)}`, 48, y)
      y += 16
    }
    const blob = doc.output('blob')
    const name = `report-${from ?? 'all'}-${to ?? 'today'}.pdf`
    const ok = await shareFile(blob, name, 'application/pdf')
    if (!ok) downloadBlob(blob, name)
  }

  return (
    <Page title="Reports" note="Gross = sale - cost - direct; Net = gross - expenses (once).">
      <div className="mb-3 grid grid-cols-3 gap-2">
        <select className={selCls} value={range} onChange={(e) => setRange(e.target.value as RangePreset)} aria-label="Range">
          <option value="today">Today</option>
          <option value="7d">7 days</option>
          <option value="30d">30 days</option>
          <option value="all">All time</option>
        </select>
        <select className={selCls} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Category">
          <option value="">Category: all</option>
          {(categories ?? []).filter((c) => !c.archived_at).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className={selCls} value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand">
          <option value="">Brand: all</option>
          <option value="samsung">Samsung</option>
          <option value="oneplus">OnePlus</option>
          <option value="apple">Apple</option>
          <option value="google">Google</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Card label="Sales value" value={formatPKR(r.salesValue)} sub={`${r.salesCount} sale(s)`} />
        <Card label="Gross profit" value={formatPKR(r.grossProfit)} tone={r.grossProfit > 0 ? 'text-emerald-400' : 'text-rose-400'} />
        <Card label="Expenses" value={formatPKR(r.expensesTotal)} tone="text-rose-400" />
        <Card label="Net profit" value={formatPKR(r.netProfit)} tone={r.netProfit > 0 ? 'text-emerald-400' : 'text-rose-400'} />
        <Card label="Receivables" value={formatPKR(r.receivables)} tone="text-amber-400" sub="current" />
        <Card label="Stock value" value={formatPKR(r.stockValue)} sub="current" />
      </div>

      {r.perCategory.length > 0 ? (
        <div className="mt-4">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">By category</h2>
          <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/40">
            {r.perCategory.map((c) => (
              <li key={c.categoryId} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-200">{c.name}</span>
                <span className="text-xs text-slate-400">
                  {c.inStockCount} stock • {formatPKR(c.stockCost, { symbol: false })} • profit {formatPKR(c.realizedProfit, { symbol: false })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button type="button" onClick={() => void exportCsv()} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-200">
          Export CSV
        </button>
        <button type="button" onClick={() => void exportPdf()} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-200">
          Export PDF
        </button>
      </div>
    </Page>
  )
}

function Card({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-0.5 text-base font-semibold tabular-nums ${tone ?? 'text-slate-100'}`}>{value}</p>
      {sub ? <p className="text-[10px] text-slate-500">{sub}</p> : null}
    </div>
  )
}
